/**
 * Custom hook to automatically map P2P API data to UI state
 * Eliminates code duplication across ActionItemTable and AssessmentTable
 * 
 * This hook centralizes the data transformation logic that was previously
 * duplicated in multiple components with useEffect patterns.
 */

import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import type { RootState } from "../../store/store";
import {
  setActionItems,
  setInitialActionItems,
  setAssessments,
  setInitialAssessments,
} from "../../store/slices/pathToPerformanceSlice";
import {
  transformP2PDataToActionItems,
  transformP2PDataToAssessments,
} from "../../utils/PathToPerformance/p2pDataTransformer";

/**
 * Hook to automatically sync P2P data to Redux state
 * Handles both action items and assessments transformation
 * 
 * @example
 * ```tsx
 * const ActionItemTable = () => {
 *   useP2PDataMapping(); // Automatically syncs data
 *   const { actionItems } = useAppSelector(state => state.pathToPerformance);
 *   // ... component logic
 * };
 * ```
 */
export const useP2PDataMapping = (): void => {
  const dispatch = useAppDispatch();
  const { p2pData, categoryLevels } = useAppSelector(
    (state: RootState) => state.pathToPerformance
  );

  /**
   * Transform and dispatch action items when P2P data or category levels change
   * Only runs when both p2pData.current and category levels are available
   */
  useEffect(() => {
    if (p2pData?.current && categoryLevels) {
      const actionItems = transformP2PDataToActionItems(
        p2pData.current,
        categoryLevels
      );
      
      dispatch(setActionItems(actionItems));
      dispatch(setInitialActionItems(actionItems));
    }
  }, [p2pData?.current, categoryLevels, dispatch]);

  /**
   * Transform and dispatch assessments when P2P data changes
   * Handles both current and previous period data
   */
  useEffect(() => {
    if (p2pData) {
      const assessments = transformP2PDataToAssessments(
        p2pData.current,
        p2pData.previous
      );
      
      dispatch(setAssessments(assessments));
      dispatch(setInitialAssessments(assessments));
    }
  }, [p2pData, dispatch]);
};
